import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase/config";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const ref = doc(db, "users", u.uid);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            setProfile({ uid: u.uid, ...snap.data() });
          } else {
            // create profile if missing
            const data = {
              email: u.email || "",
              full_name: u.displayName || "",
              role: "user",
              created_at: serverTimestamp(),
            };
            await setDoc(ref, data);
            setProfile({ uid: u.uid, ...data });
          }
        } catch (e) {
          console.error("Profile load failed", e);
          setProfile({ uid: u.uid, email: u.email, role: "user" });
        }
      } else {
        setProfile(null);
      }
      setAuthChecked(true);
    });
  }, []);

  const register = async ({ email, password, full_name, phone, phone_country, phone_dial, phone_number }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (full_name) {
      try { await updateProfile(cred.user, { displayName: full_name }); } catch (_e) { /* ignore */ }
    }
    await setDoc(doc(db, "users", cred.user.uid), {
      email,
      full_name: full_name || "",
      role: "user",
      // Phone fields (optional but populated by current register form)
      phone: phone || "",
      phone_country: phone_country || "",
      phone_dial: phone_dial || "",
      phone_number: phone_number || "",
      created_at: serverTimestamp(),
    });
    return cred.user;
  };

  const login = async ({ email, password }) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  };

  const logout = async () => {
    await fbSignOut(auth);
  };

  const isAdmin = profile?.role === "admin";

  return (
    <AuthCtx.Provider value={{ user, profile, authChecked, isAdmin, register, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}
